import { useEffect, useMemo, useState } from 'react';
import {
  ArrowSquareOutIcon,
  ArrowUpRightIcon,
  CheckCircleIcon,
  CoinIcon,
  SignatureIcon,
  SpinnerIcon,
  TargetIcon,
  WalletIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { KeyValue } from '@/components/ui/key-value';
import { Label } from '@/components/ui/label';

import {
  getWithdrawStatus,
  getWithdrawTypedData,
  relayWithdraw,
  type WithdrawStatusResponse,
  type WithdrawTypedDataResponse,
} from '../api';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';
import { getSigner } from '../wallet';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'form'; status: WithdrawStatusResponse }
  | { kind: 'quoting'; status: WithdrawStatusResponse }
  | {
      kind: 'confirm';
      status: WithdrawStatusResponse;
      quote: WithdrawTypedDataResponse;
    }
  | {
      kind: 'signing';
      status: WithdrawStatusResponse;
      quote: WithdrawTypedDataResponse;
    }
  | {
      kind: 'submitting';
      status: WithdrawStatusResponse;
      quote: WithdrawTypedDataResponse;
    }
  | {
      kind: 'submitted';
      transactionHash: string;
      state: string;
      amountUsd: string;
      toAddress: string;
    };

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
const isAddr = (s: string) => /^0x[a-fA-F0-9]{40}$/.test(s.trim());

export default function Withdraw() {
  const auth = useAuth();
  const t = useT();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [toAddress, setToAddress] = useState('');
  const [amountStr, setAmountStr] = useState('');

  useTelegramBackButton();
  useNavStackEntry({ label: t('withdraw.breadcrumb') });

  useEffect(() => {
    if (auth.state !== 'ready') return;
    let cancelled = false;
    (async () => {
      try {
        const status = await getWithdrawStatus(auth.auth.externalUserId);
        if (!cancelled) setPhase({ kind: 'form', status });
      } catch (err) {
        if (!cancelled)
          setPhase({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.state, auth.state === 'ready' ? auth.auth.externalUserId : null]);

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('withdraw.title')}
        description={t('withdraw.authenticating')}
        shortDescription={t('withdraw.authenticating')}
      />
    );
  }
  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow="Withdraw" title={t('withdraw.title')}>
        <Alert tone="danger">
          <AlertTitle>Authentication failed</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }
  if (phase.kind === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('withdraw.title')}
        description={t('withdraw.loading_balance')}
        shortDescription={t('withdraw.loading_balance')}
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: 'Balance', color: 'bg-sky-500', delay: '0.4s' },
          { label: 'Limits', color: 'bg-primary', delay: '0.8s' },
        ]}
      />
    );
  }

  if (phase.kind === 'error') {
    return (
      <ScreenShell eyebrow="Withdraw" title={t('withdraw.title')}>
        <Alert tone="danger">
          <AlertTitle>Withdrawal failed</AlertTitle>
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="lg"
          onClick={() => tryGetTelegram()?.close?.()}
        >
          {t('common.back_to_chat')}
        </Button>
      </ScreenShell>
    );
  }

  if (phase.kind === 'submitted') {
    const url = phase.transactionHash
      ? `https://polygonscan.com/tx/${phase.transactionHash}`
      : null;
    return (
      <ScreenShell eyebrow="Withdraw" title={t('withdraw.title_sent')}>
        <Card>
          <CardHeader>
            <CardEyebrow>
              <CheckCircleIcon weight="fill" className="size-3" />
              {t('withdraw.state')}: {phase.state}
            </CardEyebrow>
            <CardTitle>{t('withdraw.title_sent')}</CardTitle>
            <CardDescription>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('withdraw.sent_blurb', {
                    amount: phase.amountUsd,
                    to: short(phase.toAddress),
                  }),
                }}
              />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <KeyValue
              label={t('withdraw.amount')}
              value={`$${Number(phase.amountUsd).toFixed(2)} pUSD`}
            />
            <KeyValue
              label={t('withdraw.to')}
              value={short(phase.toAddress)}
              mono
            />
            <KeyValue
              label={t('withdraw.state')}
              value={<Badge tone="success">{phase.state}</Badge>}
            />
          </CardContent>
          <CardFooter>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'outline', size: 'lg' })}
              >
                <ArrowSquareOutIcon weight="bold" className="size-4" />
                {t('withdraw.view_polygonscan')}
              </a>
            )}
            <Button size="lg" onClick={() => tryGetTelegram()?.close?.()}>
              {t('common.back_to_chat')}
            </Button>
          </CardFooter>
        </Card>
      </ScreenShell>
    );
  }

  if (
    phase.kind === 'confirm' ||
    phase.kind === 'signing' ||
    phase.kind === 'submitting'
  ) {
    return (
      <ConfirmStep
        t={t}
        phase={phase}
        onBack={() => setPhase({ kind: 'form', status: phase.status })}
        onConfirm={async () => {
          if (auth.state !== 'ready') return;
          const externalUserId = auth.auth.externalUserId;
          try {
            setPhase({
              kind: 'signing',
              status: phase.status,
              quote: phase.quote,
            });
            const signer = await getSigner();
            const { typedData, args, withdrawalId } = phase.quote;
            const types = { ...typedData.types };
            delete (types as Record<string, unknown>)['EIP712Domain'];
            const signature = await signer.signTypedData(
              typedData.domain as never,
              types,
              typedData.message,
            );

            setPhase({
              kind: 'submitting',
              status: phase.status,
              quote: phase.quote,
            });
            const resp = await relayWithdraw(externalUserId, {
              withdrawalId,
              type: 'WALLET',
              from: args.from,
              to: args.factoryAddress,
              nonce: args.nonce,
              signature,
              depositWalletParams: {
                depositWallet: args.walletAddress,
                deadline: args.deadline,
                calls: args.calls,
              },
            });
            tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('success');
            setPhase({
              kind: 'submitted',
              transactionHash: resp.transactionHash,
              state: resp.state,
              amountUsd: phase.quote.amountUsd,
              toAddress: phase.quote.toAddress,
            });
          } catch (err) {
            tryGetTelegram()?.HapticFeedback?.notificationOccurred?.('error');
            setPhase({ kind: 'error', message: (err as Error).message });
          }
        }}
      />
    );
  }

  // phase.kind === 'form' | 'quoting'
  const status = phase.status;
  const balance = Number(status.pUsdBalance);
  const remaining = Number(status.dailyRemainingUsd);
  const maxAmount = Math.min(balance, remaining);
  const amountNum = Number(amountStr);
  const addrValid = isAddr(toAddress);
  const amtValid =
    Number.isFinite(amountNum) && amountNum > 0 && amountNum <= maxAmount;
  const canSubmit = addrValid && amtValid && phase.kind === 'form';

  const amountError = amountStr
    ? amountNum > maxAmount
      ? balance < remaining
        ? t('withdraw.exceeds_balance', { amount: balance.toFixed(2) })
        : t('withdraw.exceeds_daily', { amount: remaining.toFixed(2) })
      : !amtValid
        ? t('withdraw.enter_positive')
        : null
    : null;

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <ArrowUpRightIcon weight="fill" className="size-3" />
          {t('withdraw.title')}
        </span>
      }
      title={t('withdraw.title')}
      description="Withdraw pUSD to any Polygon address. Submitted to Polymarket's relayer — gasless and signed by your wallet."
    >
      <NavBar />
      <Card>
        <CardHeader>
          <CardEyebrow>
            <WalletIcon weight="fill" className="size-3" />
            {t('withdraw.available')}
          </CardEyebrow>
          <CardTitle className="text-3xl tabular-nums sm:text-4xl">
            ${balance.toFixed(2)}
          </CardTitle>
          <CardDescription>
            {t('withdraw.daily_limit', {
              limit: status.dailyLimitUsd.toFixed(0),
            })}{' '}
            ·{' '}
            {t('withdraw.remaining_today', { remaining: remaining.toFixed(2) })}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col gap-2">
            <Label>
              <TargetIcon weight="fill" className="size-3" />
              {t('withdraw.destination_label')}
            </Label>
            <Input
              placeholder="0x…"
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              error={!!toAddress && !addrValid}
              className="font-mono text-sm"
            />
            <p className="text-muted-foreground text-[11px] leading-relaxed">
              {t('withdraw.destination_warning')}
            </p>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <Label>
              <CoinIcon weight="fill" className="size-3" />
              {t('withdraw.amount_label')}
            </Label>
            <div className="relative">
              <Input
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                min={0}
                step="0.01"
                error={!!amountStr && !amtValid}
                className="pr-20 text-base tabular-nums"
              />
              <button
                type="button"
                onClick={() => setAmountStr(maxAmount.toFixed(2))}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl bg-primary/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground ring-1 ring-primary/30 transition-all hover:bg-primary/25 active:translate-y-[calc(-50%+1px)]"
              >
                {t('withdraw.max')}
              </button>
            </div>
            {amountError && (
              <p className="text-rose-300 text-[12px] leading-relaxed">
                {amountError}
              </p>
            )}
          </div>
        </CardContent>

        <CardFooter>
          <Button
            size="lg"
            disabled={!canSubmit}
            onClick={async () => {
              if (auth.state !== 'ready') return;
              try {
                setPhase({ kind: 'quoting', status });
                const raw = BigInt(
                  Math.round(amountNum * 1_000_000),
                ).toString();
                const quote = await getWithdrawTypedData(
                  auth.auth.externalUserId,
                  {
                    toAddress: toAddress.trim(),
                    amount: raw,
                  },
                );
                setPhase({ kind: 'confirm', status, quote });
              } catch (err) {
                setPhase({
                  kind: 'error',
                  message: (err as Error).message,
                });
              }
            }}
          >
            {phase.kind === 'quoting' ? (
              <>
                <SpinnerIcon weight="bold" className="size-4 animate-spin" />
                {t('withdraw.preparing')}
              </>
            ) : (
              t('withdraw.review')
            )}
          </Button>
        </CardFooter>
      </Card>
    </ScreenShell>
  );
}

function ConfirmStep({
  t,
  phase,
  onBack,
  onConfirm,
}: {
  t: TFunction;
  phase: Extract<Phase, { kind: 'confirm' | 'signing' | 'submitting' }>;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const { quote } = phase;
  const busy = phase.kind !== 'confirm';
  const remainingAfter = useMemo(
    () => Number(quote.dailyRemainingUsd),
    [quote.dailyRemainingUsd],
  );
  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <SignatureIcon weight="fill" className="size-3" />
          {t('withdraw.title_confirm')}
        </span>
      }
      title={t('withdraw.title_confirm')}
      description="Review the details below. Once signed, the relayer pushes the withdrawal to Polygon — gasless and final."
    >
      <Card>
        <CardHeader>
          <CardEyebrow>
            <CoinIcon weight="fill" className="size-3" />
            {t('withdraw.amount')}
          </CardEyebrow>
          <CardTitle className="text-3xl tabular-nums sm:text-4xl">
            ${Number(quote.amountUsd).toFixed(2)}
          </CardTitle>
          <CardDescription>pUSD on Polygon</CardDescription>
        </CardHeader>
        <CardContent>
          <KeyValue label={t('withdraw.to')} value={short(quote.toAddress)} mono />
          <KeyValue
            label={t('withdraw.network_fee')}
            value={<Badge tone="success">{t('withdraw.gasless')}</Badge>}
          />
          <KeyValue
            label={t('withdraw.daily_limit_label')}
            value={`$${quote.dailyLimitUsd.toFixed(0)}`}
          />
          <KeyValue
            label={t('withdraw.remaining_after')}
            value={`$${remainingAfter.toFixed(2)}`}
          />
        </CardContent>
        <CardFooter>
          <Button size="lg" disabled={busy} onClick={onConfirm}>
            {busy ? (
              <SpinnerIcon weight="bold" className="size-4 animate-spin" />
            ) : (
              <SignatureIcon weight="bold" className="size-4" />
            )}
            {phase.kind === 'signing'
              ? t('withdraw.signing')
              : phase.kind === 'submitting'
                ? t('withdraw.submitting')
                : t('withdraw.sign_send')}
          </Button>
          <Button variant="outline" size="lg" disabled={busy} onClick={onBack}>
            {t('withdraw.back')}
          </Button>
        </CardFooter>
      </Card>
    </ScreenShell>
  );
}
